// modelCache.ts
// IndexedDB-based cache for model files with TypeScript types

const DB_NAME = 'ModelCacheDB';
const STORE_NAME = 'models';
const DB_VERSION = 1;

interface ModelData {
  data: ArrayBuffer | Blob;
  timestamp: number;
  modelId: string;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    
    request.onsuccess = (event: Event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };
    
    request.onerror = (event: Event) => {
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
}

export async function getModelFromCache(modelId: string): Promise<ModelData | undefined> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(modelId);
      
      req.onsuccess = () => resolve(req.result as ModelData | undefined);
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.error('Error getting model from cache:', error);
    return undefined;
  }
}

export async function saveModelToCache(modelId: string, modelData: ArrayBuffer | Blob): Promise<void> {
  try {
    const db = await openDB();
    const data: ModelData = {
      data: modelData,
      timestamp: Date.now(),
      modelId
    };
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(data, modelId);
      
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.error('Error saving model to cache:', error);
  }
}

export async function clearModelCache(): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.clear();
      
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.error('Error clearing model cache:', error);
  }
}

export async function getModelCacheSize(): Promise<number> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAllKeys();
      
      req.onsuccess = () => resolve(req.result.length);
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.error('Error getting cache size:', error);
    return 0;
  }
}